import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import Input from '../../Components/Input'
import InputArea from '../../Components/InputArea'
import Select from '../../Components/Select'
import CustomButton from '../../Components/CustomButton'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import useInView, { animClass } from '../../Hooks/useInView'
import './styles.scss'

const templateId = process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const validate = (form) => {
  const errs = {}
  if (!form.name.trim()) errs.name = 'Name is required'
  if (!form.email.trim()) errs.email = 'Email is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errs.email = 'Enter a valid email'
  if (!form.origin.trim()) errs.origin = 'Origin is required'
  if (!form.destination.trim()) errs.destination = 'Destination is required'
  return errs
}

const Quote = () => {
  const [formRef, formInView] = useInView()
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(false)

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    cargoType: '',
    weight: '',
    date: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }))
    }
  }

  const isFormValid = Boolean(
    form.name.trim() && form.email.trim() && form.origin.trim() && form.destination.trim()
  )

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate(form)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    onChange('loading', true)
    setSubmitError(false)
    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      reset()
      setErrors({})
      setSubmitted(true)
    } catch {
      setSubmitError(true)
    }
    onChange('loading', false)
  }

  return (
    <div className="quote-page">
      <div className="quote-page__top">
        <h1>Get A Free Quote</h1>
        <p>Tell us about your shipment and we'll get back to you with pricing.</p>
      </div>

      <div className="quote-page__content">
        <div ref={formRef} className={`quote-page__card ${animClass(formInView, 'fadeInUp')}`}>
          {submitted ? (
            <div className="quote-page__success">
              <FontAwesomeIcon className="success-icon" icon={solid('circle-check')} />
              <h3>Quote Request Sent!</h3>
              <p>We'll review your shipment details and get back to you with pricing shortly.</p>
              <button className="send-again" onClick={() => { setSubmitted(false); setErrors({}); setSubmitError(false) }}>
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <div className="quote-page__error-banner">
                  <FontAwesomeIcon icon={solid('circle-exclamation')} />
                  Something went wrong. Please try again or email us at{' '}
                  <strong>business@doveglobal.us</strong>
                </div>
              )}

              <div className="form-section">
                <div className="form-section__label">Contact Info</div>
                <div className="form-grid">
                  <Input title="Full Name" required placeholder="John Doe" value={form.name} onChange={handleChange} name="name" error={errors.name} />
                  <Input title="Email Address" type="email" required placeholder="john@example.com" value={form.email} onChange={handleChange} name="email" error={errors.email} />
                  <Input title="Phone Number" placeholder="+1 (555) 000-0000" value={form.phone} onChange={handleChange} name="phone" />
                </div>
              </div>

              <div className="form-section">
                <div className="form-section__label">Shipment Details</div>
                <div className="form-grid">
                  <Input title="Origin" required placeholder="City, Country" value={form.origin} onChange={handleChange} name="origin" error={errors.origin} />
                  <Input title="Destination" required placeholder="City, Country" value={form.destination} onChange={handleChange} name="destination" error={errors.destination} />
                  <Select
                    title="Cargo Type"
                    placeholder="Select cargo type..."
                    value={form.cargoType}
                    onChange={handleChange}
                    name="cargoType"
                    options={[
                      { value: 'general', label: 'General Goods' },
                      { value: 'perishables', label: 'Perishables / Food' },
                      { value: 'electronics', label: 'Electronics' },
                      { value: 'pharmaceuticals', label: 'Pharmaceuticals' },
                      { value: 'hazmat', label: 'Hazardous Materials' },
                      { value: 'oversized', label: 'Oversized / Heavy Cargo' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                  <Select
                    title="Estimated Weight"
                    placeholder="Select weight range..."
                    value={form.weight}
                    onChange={handleChange}
                    name="weight"
                    options={[
                      { value: 'under-50', label: 'Under 50 kg' },
                      { value: '50-200', label: '50 – 200 kg' },
                      { value: '200-500', label: '200 – 500 kg' },
                      { value: '500-1000', label: '500 kg – 1 ton' },
                      { value: '1-5tons', label: '1 – 5 tons' },
                      { value: '5tons+', label: '5+ tons' },
                    ]}
                  />
                  <Input title="Preferred Pickup Date" type="date" value={form.date} onChange={handleChange} name="date" />
                </div>
              </div>

              <div className="form-section">
                <div className="form-section__label">Additional Info</div>
                <InputArea
                  title="Notes"
                  rows={4}
                  maxLength={500}
                  placeholder="Special requirements, handling instructions, or anything else we should know..."
                  value={form.msg}
                  onChange={handleChange}
                  name="msg"
                />
              </div>

              <div className="quote-page__footer">
                <div className="quote-page__footer__meta">
                  <FontAwesomeIcon icon={solid('clock')} />
                  <span>We typically respond within 1 business day</span>
                </div>
                <div className="quote-page__footer__privacy">
                  <FontAwesomeIcon icon={solid('lock')} />
                  <span>Your information is never shared with third parties.</span>
                </div>
              </div>

              <CustomButton
                loading={form.loading}
                disabled={!isFormValid || form.loading}
                label="Request Quote"
                otherClassName="submit-btn"
              />
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default Quote
